import { useLoader } from '@/composition/loader'
import useApi from '@/composition/api'
import useConfig from '@/composition/config.js'
import { useNotification } from '@/composition/notification'
import { useStore } from 'vuex'
import { useApi as useBillingApi, checkCurrencySupport } from '@frontend/simple-billing'
import { convertToSubscriptionParams, convertToPurchaseParams } from '@/modules/billing/utils/product'
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'

export function useBilling() {
	const store = useStore()
	const api = useApi()
	const config = useConfig()
	const { t } = useI18n()
	const stripeKey = store.getters['env/getProperty']('stripeApiKey')
	const webPaymentsSupported = computed(() => config.get({ key: 'web_payments_supported', default_value: false }))

	const billing = useBillingApi({
		env: store.getters['env/getEnv'],
		stripeKey
	})

	const DEFAULT_ERROR_DATA = {
		title: t('billing.error.payment_failed'),
		message: t('billing.error.insufficient_funds')
	}

	const loader = useLoader()
	const { notify } = useNotification()

	const commonData = computed(() => ({
		user_id: store.state.user.user_id,
		email: store.state.user.email,
		source: api.getPlatform()
	}))

	const canMakeWebPayments = computed(() => {
		return (currency) => {
			return webPaymentsSupported.value && checkCurrencySupport(currency) && Boolean(commonData.value.email)
		}
	})

	const createSubscription = (provider, product) => {
		loader.show()

		return billing
			.createSubscription({
				provider,
				...convertToSubscriptionParams(provider, product),
				...commonData.value
			})
			.then((subscription) => {
				store.commit('subscription/store', subscription)
				store.commit('setLastPaymentMethod', provider)

				return api.confirmSubscription(subscription, product)
			})
			.catch((error) => {
				notify({
					title: DEFAULT_ERROR_DATA.title,
					text: error?.errors?.message || error?.message || DEFAULT_ERROR_DATA.message
				})
				return Promise.reject(error)
			})
			.finally(() => {
				loader.hide()
			})
	}

	const purchase = (provider, product) => {
		loader.show()
		return billing
			.purchase({
				provider,
				...convertToPurchaseParams(provider, {
					...product,
					...commonData.value
				})
			})
			.then(() => {
				store.commit('setLastPaymentMethod', provider)
			})
			.catch((error) => {
				notify({
					title: DEFAULT_ERROR_DATA.title,
					text: error?.errors?.message || error?.message || DEFAULT_ERROR_DATA.message
				})
				return Promise.reject(error)
			})
			.finally(() => {
				loader.hide()
			})
	}

	return {
		createSubscription,
		purchase,
		canMakeWebPayments
	}
}
